import { HTTPClient } from "../generated/UserAPIAgent";

export class APIError extends Error {
    statusCode: number;
    errorCode: string;
    message: string;
    constructor(params: {
        statusCode: number;
        errorCode?: string;
        message?: string;
    }) {
        super();
        this.statusCode = params.statusCode;
        this.errorCode = params.errorCode;
        this.message = params.errorCode;
    }
}

export class ReactHttpClient implements HTTPClient {
    private baseURL: string;
    customHeaders: { [key: string]: string };
    constructor(baseURL: string) {
        this.baseURL =
            baseURL.slice(-1)[0] === "/"
                ? baseURL.slice(0, baseURL.length - 1)
                : baseURL;
        this.customHeaders = {};
    }

    async get<T>(path: string, query: { [key: string]: any }): Promise<T> {
        const res = await fetch(
            this.baseURL + path + this.createQueryString(query),
            {
                headers: {
                    ...this.customHeaders,
                    "Content-Type": "application/json",
                    Accept: "application/json, text/plain, */*"
                }
            }
        );
        return await this.handleResponse(res);
    }
    async post<T>(
        path: string,
        body: any,
        query: { [key: string]: any }
    ): Promise<T> {
        const res = await fetch(
            this.baseURL + path + this.createQueryString(query),
            {
                method: "post",
                body: JSON.stringify(body),
                headers: this.createHeaders()
            }
        );
        return await this.handleResponse(res);
    }
    async patch<T>(
        path: string,
        body: any,
        query: { [key: string]: any }
    ): Promise<T> {
        const res = await fetch(
            this.baseURL + path + this.createQueryString(query),
            {
                method: "patch",
                body: JSON.stringify(body),
                headers: this.createHeaders()
            }
        );
        return await this.handleResponse(res);
    }
    async put<T>(
        path: string,
        body: any,
        query: { [key: string]: any }
    ): Promise<T> {
        const res = await fetch(
            this.baseURL + path + this.createQueryString(query),
            {
                method: "put",
                body: JSON.stringify(body),
                headers: this.createHeaders()
            }
        );
        return await this.handleResponse(res);
    }
    async delete<T>(path: string, query: { [key: string]: any }): Promise<T> {
        const res = await fetch(
            this.baseURL + path + this.createQueryString(query),
            {
                method: "delete",
                headers: this.createHeaders()
            }
        );
        return await this.handleResponse(res);
    }

    setHeaders(headers: { [key: string]: string }): void {
        Object.keys(headers).forEach((k) => {
            this.customHeaders[k] = headers[k];
        });
    }

    private createQueryString(q: {
        [key: string]:
            | string
            | number
            | boolean
            | string[]
            | boolean[]
            | number[];
    }): string {
        const keys = Object.keys(q);
        if (!keys.length) return "";
        const parts = keys.map((k) => {
            const value = q[k];
            if (Array.isArray(value)) {
                return (value as string[]).map((v) => `${k}[]=${v}`).join("&");
            } else {
                return `${k}=${q[k]}`;
            }
        });
        return "?" + parts.join("&");
    }
    private async handleResponse(res: Response) {
        const contentType = res.headers.get("Content-Type");
        if (!contentType) {
            return null;
        }
        const types = contentType.split(";");
        const resData = await (async () => {
            if (types.includes("application/json")) {
                return await res.json();
            }
            if (types.includes("text/html")) {
                return await res.text();
            }
            return null;
        })();
        if (res.status >= 400) {
            throw new APIError(resData);
        }
        return resData;
    }

    private createHeaders() {
        return {
            ...this.customHeaders,
            "Content-Type": "application/json",
            Accept: "application/json, text/plain, */*"
        };
    }
}
